import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { fetchMyOrders } from '../services/api';
import FroggySpinner from '../components/common/FroggySpinner';
import { formatCurrency } from '../utils/currency';
import '../styles/orders.css';

const OrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchMyOrders(page);
      setOrders(response.orders);
      setPagination(response.pagination);
      
    } catch (err) {
      setError('Failed to load your orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (orderId) => {
    navigate(`/account/orders/${orderId}`);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'unpaid': 'warning',
      'PENDING': 'warning',
      'PAID': 'success',
      'AWAITING_CUSTOMER_SCHEDULING': 'info',
      'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER': 'warning',
      'AWAITING_STORE_SCHEDULING': 'primary',
      'LABEL_CREATED': 'primary',
      'SHIPPED': 'success',
      'ARRIVED': 'success',
      'DELIVERED': 'success',
      'CANCELLED': 'danger',
      'EXCEPTION': 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{getStatusDisplay(status)}</Badge>;
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'unpaid':
        return 'Pending Payment';
      case 'AWAITING_CUSTOMER_SCHEDULING':
        return 'Awaiting Schedule';
      case 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER':
        return 'Rescheduling Required';
      case 'AWAITING_STORE_SCHEDULING':
        return 'Being Prepared';
      case 'LABEL_CREATED':
        return 'Label Created';
      case 'ARRIVED':
        return 'Arrived';
      case 'EXCEPTION':
        return 'Shipping Exception';
      default:
        return status?.replace(/_/g, ' ') || 'Unknown';
    }
  };

  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return '';
    
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch (err) {
      return dateStr;
    }
  };

  const formatOrderDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading && orders.length === 0) {
    return (
      <Container className="py-5 text-center">
        <FroggySpinner size="lg" label="Loading your orders" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>My Orders</h2>
            {/* R5 B5: the store's "attention" class (btn-orange) has drawn
                the secondary look since R3; it is the ff-btn secondary now */}
            <button type="button" className="ff-btn ff-btn--secondary" onClick={() => navigate('/')}>
              Continue Shopping
            </button>
          </div>

          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
              <div className="mt-2">
                {/* a retry is not destructive — the ordinary secondary */}
                <button type="button" className="ff-btn ff-btn--secondary ff-btn--sm" onClick={() => loadOrders()}>
                  Try Again
                </button>
              </div>
            </Alert>
          )}

          {orders.length === 0 && !loading ? (
            <Card className="text-center py-5">
              <Card.Body>
                <h4>No Orders Yet</h4>
                <p className="text-muted mb-4">You haven't placed any orders yet.</p>
                <button type="button" className="ff-btn ff-btn--primary" onClick={() => navigate('/')}>
                  Start Shopping
                </button>
              </Card.Body>
            </Card>
          ) : (
            <Card>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Order #</th>
                      <th>Status</th>
                      <th>Order Date</th>
                      <th>Items</th>
                      <th className="text-end">Total Paid</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => (
                      <tr 
                        key={order.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleOrderClick(order.id)}
                      >
                        <td>
                          <span className="font-monospace text-truncate" style={{ maxWidth: '150px', display: 'inline-block' }}>
                            {order.id}
                          </span>
                        </td>
                        <td>
                          <div>
                            {getStatusBadge(order.status)}
                            {order.delivery_date && (
                              <div className="text-muted small mt-1">
                                Expected: {formatDeliveryDate(order.delivery_date)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {formatOrderDate(order.order_date)}
                        </td>
                        <td>
                          {order.item_count} item{order.item_count !== 1 ? 's' : ''}
                        </td>
                        <td className="text-end">
                          <strong>{formatCurrency(order.total)}</strong>
                        </td>
                        <td>
                          {/* Batch 34 C1: a per-row action is never a primary —
                              ten filled "View Details" were a wall of blue.
                              The row's own act is a quiet secondary (sm, 30px). */}
                          <button
                            type="button"
                            className="ff-btn ff-btn--secondary ff-btn--sm"
                            data-order-view-details
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOrderClick(order.id);
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <button
                type="button"
                className="ff-btn ff-btn--secondary me-2"
                disabled={!pagination.has_previous}
                onClick={() => loadOrders(pagination.current_page - 1)}
              >
                Previous
              </button>
              <span className="align-self-center mx-3">
                Page {pagination.current_page} of {pagination.total_pages}
              </span>
              <button
                type="button"
                className="ff-btn ff-btn--secondary"
                disabled={!pagination.has_next}
                onClick={() => loadOrders(pagination.current_page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default OrdersPage;